/**
 * <sb-creator-application> — Southerly Beauty creator collaboration form.
 *
 * Single page. Validates on submit, POSTs one JSON object to the configured endpoint,
 * then swaps the form for the success panel.
 *
 * The endpoint is read from the `data-endpoint` attribute (set from the section setting
 * in sb-creator-application.liquid). If it is empty the form runs in preview mode: it
 * validates and shows the success panel but posts nothing, and logs the payload to the
 * console so the shape can be checked before wiring Zapier.
 *
 * Cloudflare Turnstile is optional and inert by default. Leave the section's Turnstile
 * site key blank and no script loads, no widget renders, and nothing changes. Fill it in
 * and the widget appears above the submit button and a token is required — point the
 * endpoint at the Cloudflare Worker in that case, since the token must be verified
 * server-side and Zapier cannot do it.
 *
 * Deliberately NOT sent from the browser: status, dm_verified, application_source,
 * product, shopify_draft_order_id, tracking, published_url. This endpoint is a public URL
 * and anything posted to it is attacker-controlled — if the workflow trusted a
 * client-supplied status, forging "Approved" would ship a free product to any address.
 * Those are set as static values inside the intake automation instead.
 */
(function () {
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  class SBCreatorApplication extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-sbca-form]');
      if (!this.form) return;

      this.endpoint = (this.dataset.endpoint || '').trim();
      this.siteKey = (this.dataset.turnstileSitekey || '').trim();
      this.turnstileToken = '';
      this.widgetId = null;
      this.openedAt = Date.now();
      this.submitting = false;

      this.summaryEl = this.querySelector('[data-sbca-summary]');
      this.submitErrorEl = this.querySelector('[data-sbca-submit-error]');
      this.successEl = this.querySelector('[data-sbca-success]');
      this.submitBtn = this.querySelector('[data-sbca-submit]');

      var self = this;
      this.form.addEventListener('submit', function (event) {
        event.preventDefault();
        self.submit();
      });

      /* Clear a field's error as soon as the person starts fixing it. */
      this.form.addEventListener('input', function (e) { self.clearFieldError(e.target); });
      this.form.addEventListener('change', function (e) { self.clearFieldError(e.target); });

      this.loadTurnstile();
    }

    /* ------------------------------------------------------------ errors */

    fieldWrap(el) {
      return el && el.closest ? el.closest('.sbca__field') : null;
    }

    setFieldError(el, message) {
      var wrap = this.fieldWrap(el);
      if (!wrap) return;
      var error = wrap.querySelector('[data-sbca-error]');
      if (error) {
        error.textContent = message;
        error.classList.add('is-visible');
      }
      wrap.classList.add('has-error');
      if (el.type !== 'radio' && el.type !== 'checkbox') {
        el.setAttribute('aria-invalid', 'true');
      }
    }

    clearFieldError(el) {
      var wrap = this.fieldWrap(el);
      if (!wrap) return;
      var error = wrap.querySelector('[data-sbca-error]');
      if (error) {
        error.textContent = '';
        error.classList.remove('is-visible');
      }
      wrap.classList.remove('has-error');
      wrap.querySelectorAll('[aria-invalid]').forEach(function (n) {
        n.removeAttribute('aria-invalid');
      });
      if (wrap.querySelector('[data-sbca-social]')) this.hideSocialError();
    }

    showSummary(message) {
      if (!this.summaryEl) return;
      this.summaryEl.textContent = message;
      this.summaryEl.classList.add('is-visible');
    }

    hideSummary() {
      if (!this.summaryEl) return;
      this.summaryEl.textContent = '';
      this.summaryEl.classList.remove('is-visible');
    }

    /**
     * Bring an element into view clear of the theme's fixed nav, then focus it without
     * letting the browser scroll again — a second scroll cancels the smooth one.
     */
    scrollTo(el, focus) {
      if (!el) return;
      var top = el.getBoundingClientRect().top + window.pageYOffset - 120;
      window.scrollTo({ top: top < 0 ? 0 : top, behavior: 'smooth' });
      if (focus !== false) {
        try { el.focus({ preventScroll: true }); } catch (e) { /* older browsers */ }
      }
    }

    /** Failure to reach the endpoint — shown next to the button, not at the top. */
    showSubmitError(message) {
      var el = this.submitErrorEl || this.summaryEl;
      if (!el) return;
      el.textContent = message;
      el.classList.add('is-visible');
      this.scrollTo(el, false);
    }

    hideSubmitError() {
      if (!this.submitErrorEl) return;
      this.submitErrorEl.textContent = '';
      this.submitErrorEl.classList.remove('is-visible');
    }

    showSocialError(message) {
      var el = this.querySelector('[data-sbca-social-error]');
      if (!el) return;
      el.textContent = message;
      el.classList.add('is-visible');
      /* The rule applies to both accounts, so mark both rather than leaving the
         message pointing at nothing. */
      this.form.querySelectorAll('[data-sbca-social]').forEach(function (input) {
        input.setAttribute('aria-invalid', 'true');
      });
    }

    hideSocialError() {
      var el = this.querySelector('[data-sbca-social-error]');
      if (!el) return;
      el.textContent = '';
      el.classList.remove('is-visible');
      this.form.querySelectorAll('[data-sbca-social]').forEach(function (input) {
        input.removeAttribute('aria-invalid');
      });
    }

    /* -------------------------------------------------------- validation */

    validate() {
      var self = this;
      var firstBad = null;
      var seenGroups = {};

      this.form.querySelectorAll('[data-sbca-input]').forEach(function (el) {
        if (el.type === 'radio') {
          if (seenGroups[el.name] || !el.required) return;
          seenGroups[el.name] = true;
          if (!self.form.querySelector('input[name="' + CSS.escape(el.name) + '"]:checked')) {
            self.setFieldError(el, 'Please choose an option.');
            if (!firstBad) firstBad = el;
          }
          return;
        }

        if (el.type === 'checkbox') {
          if (el.required && !el.checked) {
            self.setFieldError(el, 'Please tick this box to continue.');
            if (!firstBad) firstBad = el;
          }
          return;
        }

        var value = (el.value || '').trim();

        if (el.required && !value) {
          self.setFieldError(el, 'This field is required.');
          if (!firstBad) firstBad = el;
          return;
        }
        if (!value) return;

        if (el.type === 'email' && !EMAIL_RE.test(value)) {
          self.setFieldError(el, 'Please enter a valid email address.');
          if (!firstBad) firstBad = el;
          return;
        }
        if (el.name === 'shipping_postcode' && !/^\d{4}$/.test(value)) {
          self.setFieldError(el, 'New Zealand post codes are four digits.');
          if (!firstBad) firstBad = el;
        }
      });

      /* At least one social account. */
      var socials = this.form.querySelectorAll('[data-sbca-social]');
      var hasSocial = false;
      socials.forEach(function (el) { if ((el.value || '').trim()) hasSocial = true; });
      if (!hasSocial) {
        this.showSocialError('Please add at least one account so we can find your work.');
        if (!firstBad) firstBad = socials[0];
      } else {
        this.hideSocialError();
      }

      if (firstBad) {
        this.showSummary('Please check the highlighted fields below.');
        /* Scroll to the field itself, not the banner — the banner is at the very top of a
           long page and tells them nothing about which field to fix. */
        this.scrollTo(this.fieldWrap(firstBad) || firstBad, false);
        try { firstBad.focus({ preventScroll: true }); } catch (e) { /* older browsers */ }
        return false;
      }

      this.hideSummary();
      return true;
    }

    /* --------------------------------------------------------- turnstile */

    loadTurnstile() {
      if (!this.siteKey || this.turnstileRequested) return;
      this.turnstileRequested = true;

      var self = this;
      if (window.turnstile) { this.renderTurnstile(); return; }

      window.sbcaTurnstileReady = function () { self.renderTurnstile(); };
      var script = document.createElement('script');
      script.src =
        'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=sbcaTurnstileReady';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    renderTurnstile() {
      var container = this.querySelector('[data-sbca-turnstile]');
      if (!container || !window.turnstile || this.widgetId !== null) return;
      container.hidden = false;
      var self = this;
      this.widgetId = window.turnstile.render(container, {
        sitekey: this.siteKey,
        callback: function (token) { self.turnstileToken = token; self.hideSummary(); },
        'expired-callback': function () { self.turnstileToken = ''; },
        'error-callback': function () { self.turnstileToken = ''; }
      });
    }

    resetTurnstile() {
      this.turnstileToken = '';
      if (window.turnstile && this.widgetId !== null) {
        try { window.turnstile.reset(this.widgetId); } catch (e) { /* noop */ }
      }
    }

    /* -------------------------------------------------------- submission */

    buildPayload() {
      var data = new FormData(this.form);
      var payload = {};

      data.forEach(function (value, key) {
        if (key === 'website_url') return; /* honeypot */
        payload[key] = typeof value === 'string' ? value.trim() : value;
      });

      /* Unticked checkboxes are absent from FormData — record them as false. */
      this.form.querySelectorAll('input[type="checkbox"]').forEach(function (el) {
        if (el.name) payload[el.name] = !!el.checked;
      });

      /**
       * Shopify wants a bare province code (WGN), and its own region labels don't match
       * correct New Zealand spelling — it calls MWT "Manawatu-Wanganui". So emit the code
       * from the form rather than matching on names downstream. shipping_region keeps the
       * readable name for the table; shipping_region_code goes to Shopify.
       */
      var region = this.form.querySelector('[data-sbca-region]');
      if (region) {
        var selected = region.options[region.selectedIndex];
        payload.shipping_region_code = (selected && selected.dataset.regionCode) || '';
      }

      payload.submitted_at = new Date().toISOString();
      payload.page_url = window.location.href;

      /* Consumed and stripped by the Worker; harmless when posting straight to Zapier. */
      payload.sbca_form_opened_at = this.openedAt;
      if (this.siteKey) payload.turnstile_token = this.turnstileToken;

      return payload;
    }

    submit() {
      if (this.submitting) return;
      this.hideSubmitError();
      if (!this.validate()) return;

      if (this.siteKey && !this.turnstileToken) {
        this.showSubmitError('Please complete the verification check, then submit again.');
        return;
      }

      /* Honeypot: silently succeed for bots. */
      var honey = this.form.querySelector('input[name="website_url"]');
      if (honey && honey.value) { this.showSuccess(); return; }

      var payload = this.buildPayload();
      var self = this;

      if (!this.endpoint) {
        console.info('[sb-creator-application] No endpoint configured — preview mode. Payload:', payload);
        this.showSuccess();
        return;
      }

      this.submitting = true;
      if (this.submitBtn) {
        this.submitBtn.disabled = true;
        this.submitBtn.textContent = 'Sending…';
      }

      /**
       * Zapier catch hooks send no CORS headers and offer no way to configure them, so a
       * JSON POST fails at the preflight: `Content-Type: application/json` makes the
       * request non-simple, the browser asks permission first, and Zapier's preflight
       * response doesn't allow that header. The request never leaves the browser.
       *
       * Posting form-encoded avoids the preflight entirely — it's one of the three
       * content types CORS treats as simple — so the request goes straight through and
       * Zapier parses it into named fields natively. The catch is that without
       * Access-Control-Allow-Origin on the reply we still can't *read* the response, so
       * the request is sent no-cors and we accept that a server-side failure is
       * indistinguishable from success. Client-side validation has already run, so what
       * remains is Zapier being down.
       *
       * Any other endpoint — the Cloudflare Worker, which does set CORS headers — keeps
       * the JSON path with real error handling.
       */
      if (this.isCorsBlindEndpoint()) {
        var params = new URLSearchParams();
        Object.keys(payload).forEach(function (key) { params.append(key, payload[key]); });

        fetch(this.endpoint, { method: 'POST', mode: 'no-cors', body: params })
          .then(function () { self.showSuccess(); })
          .catch(function (error) {
            console.error('[sb-creator-application] Submission failed', error);
            self.restoreSubmit();
            self.showSubmitError(
              'Sorry — we couldn’t send that just then. Please check your connection and try again, or email hello@southerlybeauty.co.nz.'
            );
          });
        return;
      }

      fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (response) {
          return response.json().catch(function () { return {}; }).then(function (data) {
            if (!response.ok) {
              var err = new Error('Request failed: ' + response.status);
              err.friendly = data && data.error;
              throw err;
            }
            self.showSuccess();
          });
        })
        .catch(function (error) {
          console.error('[sb-creator-application] Submission failed', error);
          self.restoreSubmit();
          self.showSubmitError(
            error.friendly ||
              'Sorry — we couldn’t send that just then. Please try again, or email hello@southerlybeauty.co.nz and we’ll sort it out.'
          );
        });
    }

    /**
     * True for endpoints known to reply without CORS headers, so the response can't be
     * read. Currently just Zapier's catch hooks — the Worker sets them properly.
     */
    isCorsBlindEndpoint() {
      try {
        return /(^|\.)hooks\.zapier\.com$/i.test(new URL(this.endpoint, window.location.href).hostname);
      } catch (e) {
        return false;
      }
    }

    restoreSubmit() {
      this.submitting = false;
      this.resetTurnstile();
      if (this.submitBtn) {
        this.submitBtn.disabled = false;
        this.submitBtn.textContent = 'Count me in';
      }
    }

    showSuccess() {
      var success = this.successEl;
      if (!success) return;

      /* Hide everything in the form except the success panel and the honeypot. */
      Array.prototype.forEach.call(this.form.children, function (child) {
        if (child !== success && !child.classList.contains('sbca__hp')) child.hidden = true;
      });
      success.hidden = false;

      var top = this.getBoundingClientRect().top + window.pageYOffset - 90;
      window.scrollTo({ top: top < 0 ? 0 : top, behavior: 'smooth' });
      success.focus({ preventScroll: true });
    }
  }

  if (!customElements.get('sb-creator-application')) {
    customElements.define('sb-creator-application', SBCreatorApplication);
  }
})();
